import React from "react";
import { Typography } from "antd";
import { useStudyPlan } from "../../../app/contexts/study.context";
import "./index.css";

// This component displays the number of activated sites out of the total required sites for a study plan. 
// It can also show the remaining sites needed to meet the requirement. 
// The style changes based on whether the activated sites meet the required sites or not. 
// The component is memoized to prevent unnecessary re-renders when the props do not change.
// It sets a default studyPlanType of 2, which can be overridden by passing a different value as a prop.
export const ActivatedSitesStats = React.memo(
    (props) => {       
        const { siteActivationStats } = useStudyPlan();
        let params = {...props, siteActivationStats};
        if (props.studyPlanType === 1) {
            return <RatesPlanStats {...params} />;
        }
        else if (props.studyPlanType === 2) {
            return <DurationPlanStats {...params} />;
        }
        
        return null;
    }
  );

const DurationPlanStats = (props) => {
    const contextActivated = Number(props.siteActivationStats?.sitesActivated) || 0;
    const contextRequired = Number(props.siteActivationStats?.totalSitesRequired) || 0;

    const activeSites =
        props.overrides?.activatedTotal === null || props.overrides?.activatedTotal === undefined
            ? contextActivated
            : Number(props.overrides?.activatedTotal) || 0;
    const requiredSites =
        props.overrides?.requiredTotal === null || props.overrides?.requiredTotal === undefined
            ? contextRequired
            : Number(props.overrides.requiredTotal) || 0;

    const remainingSites = requiredSites - activeSites;

    const style = activeSites === requiredSites ? "info" : "warning";

    return renderInternal(
        `sites entered ${activeSites} out of ${requiredSites}, remaining ${remainingSites}`,
        style
    );
}

const RatesPlanStats = (props) => {
    const contextSitesRequired = props.siteActivationStats?.totalSitesRequired || 0;
    const totalLowerBound = props.siteActivationStats?.sitesActivatedLowerBound || 0;
    const totalUpperBound = props.siteActivationStats?.sitesActivatedUpperBound || 0;

    const requiredSites =
        props.overrides?.requiredTotal === null || props.overrides?.requiredTotal === undefined
            ? contextSitesRequired
            : Number(props.overrides?.requiredTotal) || 0;

    const lowerBound
        = props.overrides?.lowerTotal === null || props.overrides?.lowerTotal === undefined
            ? totalLowerBound
            : Number(props.overrides?.lowerTotal) || 0;

    const upperBound
        = props.overrides?.upperTotal === null || props.overrides?.upperTotal === undefined
            ? totalUpperBound
            : Number(props.overrides?.upperTotal) || 0;
    
    const { text, style } = adjustRatesStatsDisplay(lowerBound, upperBound, requiredSites);
    const fullText = `${lowerBound} - ${upperBound} of ${requiredSites}, ${text}`;

    return renderInternal(fullText, style);
}

const renderInternal = (text, style) => {
    return (
        <Typography className={`activated-sites-stats ${style}`}>
            <i className={'small material-icons'}>{style}</i>
            {text}
        </Typography>
    );
}

const adjustRatesStatsDisplay = (lowerBound, upperBound, requiredSites) => {
    if (lowerBound <= requiredSites && upperBound >= requiredSites) {
        return {"text": "overall sites within country range", "style":"info"};
    } else if (lowerBound > requiredSites) {
        return {"text": `reduce lower by ${lowerBound - requiredSites} across countries`, "style":"warning"};
    } else if (upperBound < requiredSites) {
        return {"text": `increase upper by ${requiredSites - upperBound} across countries`, "style":"warning"};
    }
}
